import React, { useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { ShieldCheck, Cloud, ShoppingBag, FolderKanban } from 'lucide-react';
import ParticleBackground from './components/ParticleBackground';
import ServiceCard from './components/ServiceCard';
import Footer from './components/Footer';
import NetworkStatus from './components/NetworkStatus';
import { ServiceItem } from './types';

// Data Configuration
const services: ServiceItem[] = [
  {
    id: '1',
    title: 'Premium Access',
    description: 'Exclusive access to Gemini, ChatGPT, and enterprise SaaS accounts. Secure & Instant.',
    icon: ShieldCheck,
    href: 'https://accounts.zentix.shop',
    status: 'active',
    color: 'cyan'
  },
  {
    id: '2',
    title: 'Zentix Cloud',
    description: 'High-speed, encrypted storage solutions for your digital assets. Zero-knowledge architecture.',
    icon: Cloud,
    href: 'https://drive.zentix.shop',
    status: 'active',
    color: 'purple'
  },
  {
    id: '3',
    title: 'Digital Store',
    description: 'Marketplace for scripts, software, and development tools. Automated delivery.',
    icon: ShoppingBag,
    href: 'https://store.zentix.shop',
    status: 'active',
    color: 'cyan'
  },
  {
    id: '4',
    title: 'Project X',
    description: 'Next-generation AI automated workflow tools. Currently in closed beta.',
    icon: FolderKanban,
    href: '#',
    status: 'coming-soon',
    color: 'gray'
  }
];

// Animation Variants
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.2
    }
  }
};

const letterVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      stiffness: 200
    }
  }
};

const wordContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 1.2 // Starts after headline finishes mostly
    }
  }
};

const wordVariants: Variants = {
  hidden: { opacity: 0, filter: 'blur(10px)', y: 10 },
  visible: {
    opacity: 1,
    filter: 'blur(0px)',
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const App: React.FC = () => {
  const [isNetworkOpen, setIsNetworkOpen] = useState(false);

  // Helper to split text for animations
  const titleText = "The Digital";
  const nexusText = "Nexus";
  const subtitleText = "Zentix is your gateway to premium digital infrastructure. Accounts, Cloud, and Assets—unified in one ecosystem.";

  return (
    <div className="min-h-screen bg-void text-white font-sans selection:bg-cyan-500/30 selection:text-cyan-200 relative">
      
      {/* Background Layer */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-black to-black z-0" />
      <div className="fixed inset-0 z-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 brightness-100 contrast-150 mix-blend-overlay"></div>
      <ParticleBackground />

      {/* Main Content */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-6 pt-20 pb-10">
        
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="inline-flex items-center justify-center px-3 py-1 mb-8 border border-cyan-500/30 rounded-full bg-cyan-950/20 backdrop-blur-sm"
          >
            <span className="w-2 h-2 mr-2 bg-cyan-400 rounded-full animate-pulse"></span>
            <span className="text-xs font-semibold tracking-widest text-cyan-300 uppercase">System Online</span>
          </motion.div>

          <motion.h1
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-5xl md:text-7xl font-display font-bold mb-8 tracking-tight leading-tight"
          >
            {/* "The Digital" - Staggered Letters */}
            <span className="inline-block mr-3 md:mr-4">
              {titleText.split("").map((char, index) => (
                <motion.span key={index} variants={letterVariants} className="inline-block">
                  {char === " " ? "\u00A0" : char}
                </motion.span>
              ))}
            </span>
            <br className="md:hidden" />
            
            {/* "Nexus" - Gradient & Staggered Letters */}
            <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 animate-gradient-x pb-2">
              {nexusText.split("").map((char, index) => (
                <motion.span key={index} variants={letterVariants} className="inline-block">
                  {char}
                </motion.span>
              ))}
            </span>
          </motion.h1>

          <motion.p
            variants={wordContainerVariants}
            initial="hidden"
            animate="visible"
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed flex flex-wrap justify-center gap-x-1.5"
          >
            {subtitleText.split(" ").map((word, index) => (
              <motion.span key={index} variants={wordVariants} className="inline-block">
                {word}
              </motion.span>
            ))}
          </motion.p>
        </div>

        {/* Portal Grid */}
        <div className="w-full max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.8, ease: "easeOut" }} // Delays until text finishes
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((item, index) => (
              <ServiceCard key={item.id} item={item} index={index} />
            ))}
          </motion.div>
        </div>

      </main>

      <Footer onOpenNetworkStatus={() => setIsNetworkOpen(true)} />

      <NetworkStatus isOpen={isNetworkOpen} onClose={() => setIsNetworkOpen(false)} />
    </div>
  );
};

export default App;